import { useState, useEffect, useMemo } from 'react'
import useBodyScrollLock from '../../../../hooks/useBodyScrollLock'
import { TextAreaField } from './FieldComponents'
import AppDropdown from '../../../../components/AppDropdown'
import AppDateInput from '../../../../components/AppDateInput'
import { getStudentInstallments, createStudentReceipt, downloadStudentReceipt, openStudentReceipt } from '../api/studentScreensApi'

function Label({ children, required }) {
  return (
    <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
      {children}
      {required ? <span className="text-error"> *</span> : null}
    </span>
  )
}

function InputField({ label, required, disabled, type = 'text', value, onChange, placeholder, ...rest }) {
  if (type === 'date') {
    return (
      <div className="grid gap-1.5">
        <Label required={required}>{label}</Label>
        <AppDateInput
          value={value || ''}
          onChange={(val) => onChange && onChange({ target: { value: val } })}
          placeholder={placeholder || 'DD-MM-YYYY'}
          disabled={disabled}
        />
      </div>
    )
  }

  return (
    <label className="grid gap-1.5">
      <Label required={required}>{label}</Label>
      <input
        type={type}
        value={value || ''}
        onChange={onChange}
        placeholder={placeholder}
        disabled={disabled}
        className="h-9 w-full rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-3 py-2 text-xs text-on-surface outline-none transition-all placeholder:text-on-surface-variant/50 focus:border-primary focus:bg-surface focus:ring-2 focus:ring-primary/20 disabled:opacity-60"
        {...rest}
      />
    </label>
  )
}

function SelectFieldInline({ label, required, options = [], disabled, value, onChange }) {
  const normOptions = useMemo(() => {
    return (options || []).map((o) =>
      typeof o === 'object' && o !== null ? o : { value: String(o), label: String(o) }
    )
  }, [options])

  return (
    <div className="grid gap-1.5">
      <Label required={required}>{label}</Label>
      <AppDropdown
        value={value || ''}
        onChange={(val) => onChange && onChange({ target: { value: val } })}
        options={normOptions}
        disabled={disabled}
        triggerClassName="!h-9 !rounded-xl !border-outline-variant/30 !bg-surface-container-lowest !text-xs !text-on-surface hover:!bg-surface-container transition-all"
      />
    </div>
  )
}

function fmt(n) {
  return Number(n || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function isoDate(d) {
  return new Date(d).toISOString().slice(0, 10)
}

export default function ReceiptEntryForm({ student, studentId, onBack, onSaved }) {
  useBodyScrollLock(true)

  const [form, setForm] = useState({
    acHead: '',
    cashBank: 'Cash',
    mode: 'Cash',
    instNo: '',
    instDate: '',
    drawnOn: '',
    billAmt: '',
    company: student?.institute || student?.company || '',
    branch: student?.branch || '',
    dept: '',
    type: 'Cash',
    no: 'Auto',
    date: isoDate(new Date()),
    place: student?.place || '',
    narration: '',
  })

  const [selectedInstallments, setSelectedInstallments] = useState(new Set())
  const [paymentAmounts, setPaymentAmounts] = useState({})
  const [writeOff, setWriteOff] = useState({})
  const [bankCharges, setBankCharges] = useState('0.00')
  const [gstAmount, setGstAmount] = useState('0.00')
  const [tds, setTds] = useState('0.00')
  const [fileName, setFileName] = useState('')
  const [showHistory, setShowHistory] = useState(false)
  const [installments, setInstallments] = useState([])
  const [loadingBills, setLoadingBills] = useState(false)
  const [billsMsg, setBillsMsg] = useState('')
  const [saving, setSaving] = useState(false)
  const [saveMsg, setSaveMsg] = useState('')
  const [history, setHistory] = useState([])
  const [loadingHistory, setLoadingHistory] = useState(false)

  const sid = String(studentId || student?.id || '')

  const totals = useMemo(() => {
    let total = 0
    selectedInstallments.forEach((id) => {
      const inst = installments.find((i) => i.id === id)
      if (inst) {
        const paid = Number(paymentAmounts[id]) || 0
        total += paid || inst.balance
      }
    })
    return {
      total: fmt(total + Number(bankCharges) + Number(gstAmount) - Number(tds)),
      amount: fmt(total),
    }
  }, [selectedInstallments, paymentAmounts, bankCharges, gstAmount, tds, installments])

  const rawTotals = useMemo(() => {
    let total = 0
    selectedInstallments.forEach((id) => {
      const inst = installments.find((i) => i.id === id)
      if (inst) {
        const paid = Number(paymentAmounts[id]) || 0
        total += paid || inst.balance
      }
    })
    return {
      amount: total,
      net: total + (Number(bankCharges) || 0) + (Number(gstAmount) || 0) - (Number(tds) || 0),
    }
  }, [selectedInstallments, paymentAmounts, bankCharges, gstAmount, tds, installments])

  const toggleInstallment = (id) => {
    setSelectedInstallments((prev) => {
      const next = new Set(prev)
      if (next.has(id)) {
        next.delete(id)
        setPaymentAmounts((p) => { const n = { ...p }; delete n[id]; return n })
      } else {
        next.add(id)
        const inst = installments.find((i) => i.id === id)
        if (inst) setPaymentAmounts((p) => ({ ...p, [id]: String(inst.balance) }))
      }
      return next
    })
  }

  const selectAll = () => {
    const all = new Set(installments.map((i) => i.id))
    setSelectedInstallments(all)
    const amt = {}
    installments.forEach((i) => { amt[i.id] = String(i.balance) })
    setPaymentAmounts(amt)
  }

  const clearAll = () => {
    setSelectedInstallments(new Set())
    setPaymentAmounts({})
  }

  const handleGetBills = async () => {
    setLoadingBills(true)
    setBillsMsg('')
    try {
      const rows = await getStudentInstallments(sid)
      const orderNo = student?.studentCode ? `ADM-${student.studentCode}` : `ADM-${sid || 'NEW'}`
      const pending = (rows || [])
        .map((i, idx) => ({
          id: i.id,
          no: idx + 1,
          date: (i.dueDate || '').slice(0, 10),
          dueDate: (i.dueDate || '').slice(0, 10),
          orderNo,
          amount: Number(i.amount) || 0,
          balance: Number(i.balance != null ? i.balance : i.amount) || 0,
          description: i.particulars || `Installment ${idx + 1}`,
        }))
      setInstallments(pending)
      if (pending.length > 0) {
        setSelectedInstallments(new Set(pending.map((i) => i.id)))
        setPaymentAmounts(Object.fromEntries(pending.map((i) => [i.id, String(i.balance)])))
      } else {
        setSelectedInstallments(new Set())
        setPaymentAmounts({})
      }
    } catch {
      setInstallments([])
      setSelectedInstallments(new Set())
      setPaymentAmounts({})
      setBillsMsg('Unable to load pending bills. Please try again.')
    } finally {
      setLoadingBills(false)
    }
  }

  useEffect(() => {
    handleGetBills()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sid])

  const handleSave = async () => {
    if (selectedInstallments.size === 0 || !(rawTotals.amount > 0)) {
      setSaveMsg('Please select at least one installment with a payment amount.')
      return
    }

    setSaving(true)
    setSaveMsg('')
    try {
      await createStudentReceipt({
        studentId: sid || '114052',
        amount: String(rawTotals.amount),
        totalAmount: String(rawTotals.net),
        mode: form.mode,
        type: form.type,
        cashBank: form.cashBank,
        instNo: form.instNo,
        instDate: form.instDate,
        date: form.date,
        drawnOn: form.drawnOn,
        place: form.place,
        narration: form.narration,
        company: form.company,
        branch: form.branch,
        dept: form.dept,
      })
      onSaved && onSaved()
    } catch (err) {
      setSaveMsg(err?.message || 'Failed to save receipt.')
    } finally {
      setSaving(false)
    }
  }

  const openHistory = async () => {
    setShowHistory(true)
    setLoadingHistory(true)
    try {
      const rows = await getStudentInstallments(sid)
      setHistory(rows || [])
    } catch {
      setHistory([])
    } finally {
      setLoadingHistory(false)
    }
  }

  const studentFullName = student ? `${student.firstName || ''} ${student.lastName || ''}`.trim() : 'Student'

  return (
    <div className="mx-auto flex w-full max-w-[1360px] flex-col gap-5 pb-8">
      {/* ── Title Banner ── */}
      <div className="flex items-center justify-between rounded-2xl bg-primary px-6 py-4 shadow-sm text-on-primary">
        <div className="flex items-center gap-3">
          <span className="material-symbols-outlined text-[24px]">receipt_long</span>
          <div>
            <h2 className="text-base font-bold">Record Student Receipt</h2>
            <p className="text-xs opacity-85">New fee collection & installment reconciliation</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-xl bg-on-primary/15 px-3.5 py-1.5 text-xs font-bold">
            {studentFullName} {sid ? `#${sid}` : ''}
          </span>
        </div>
      </div>

      {showHistory ? (
        <div className="overflow-hidden rounded-2xl border border-outline-variant/30 bg-surface-container-lowest shadow-sm">
          <div className="flex items-center justify-between border-b border-outline-variant/20 bg-surface-container-low px-5 py-3.5">
            <h3 className="text-xs font-black uppercase tracking-wider text-on-surface-variant">Receipt History</h3>
            <button
              type="button"
              onClick={() => setShowHistory(false)}
              className="rounded-xl bg-primary/10 px-3 py-1.5 text-xs font-bold text-primary hover:bg-primary/20 transition-colors cursor-pointer"
            >
              Close History
            </button>
          </div>
          {loadingHistory ? (
            <div className="px-5 py-12 text-center text-xs text-on-surface-variant">Loading receipts...</div>
          ) : history.length === 0 ? (
            <div className="px-5 py-12 text-center">
              <p className="text-sm font-semibold text-on-surface-variant">No previous receipts recorded.</p>
            </div>
          ) : (
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-outline-variant/20 bg-surface-container-low">
                  {['Action', 'Receipt No.', 'Date', 'Type', 'Amount', 'Mode', 'Narration'].map((h) => (
                    <th key={h} className="px-4 py-2.5 font-bold uppercase tracking-wider text-on-surface-variant">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map((r) => (
                  <tr key={r.id} className="border-b border-outline-variant/10 hover:bg-surface-container-low/40">
                    <td className="px-4 py-2.5">
                      <button
                        type="button"
                        onClick={() => openStudentReceipt(r.id)}
                        className="inline-flex items-center gap-1 rounded-lg bg-primary/10 px-2.5 py-1 text-xs font-bold text-primary hover:bg-primary/20"
                      >
                        <span className="material-symbols-outlined text-[14px]">print</span>
                        Print
                      </button>
                    </td>
                    <td className="px-4 py-2.5 font-bold">{r.no || r.id}</td>
                    <td className="px-4 py-2.5">{r.date || '—'}</td>
                    <td className="px-4 py-2.5 font-semibold">{r.type || 'Cash'}</td>
                    <td className="px-4 py-2.5 font-mono font-bold">₹{fmt(r.amount)}</td>
                    <td className="px-4 py-2.5">{r.mode || 'Cash'}</td>
                    <td className="px-4 py-2.5 truncate max-w-xs">{r.narration || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      ) : (
        <>
          {/* ── Three Panels: Payment, Instrument, Voucher ── */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            {/* 1. Payment Details */}
            <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-5 shadow-sm space-y-4">
              <h3 className="text-xs font-black uppercase tracking-[0.14em] text-primary border-b border-outline-variant/15 pb-2">
                Payment Details
              </h3>
              <div className="space-y-3.5">
                <label className="grid gap-1.5">
                  <Label>A/c Head</Label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={`${studentFullName} ${sid ? `#${sid}` : ''}`}
                      readOnly
                      className="flex-1 h-9 rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none"
                    />
                  </div>
                </label>
                <SelectFieldInline
                  label="Cash / Bank"
                  required
                  options={['Cash', 'HDFC Bank', 'SBI Bank', 'ICICI Bank']}
                  value={form.cashBank}
                  onChange={(e) => setForm({ ...form, cashBank: e.target.value })}
                />
                <SelectFieldInline
                  label="Mode"
                  required
                  options={['Cash', 'Cheque', 'DD', 'NEFT', 'RTGS', 'UPI']}
                  value={form.mode}
                  onChange={(e) => setForm({ ...form, mode: e.target.value })}
                />
              </div>
            </div>

            {/* 2. Instrument Details */}
            <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-5 shadow-sm space-y-4">
              <h3 className="text-xs font-black uppercase tracking-[0.14em] text-primary border-b border-outline-variant/15 pb-2">
                Instrument Details
              </h3>
              <div className="space-y-3.5">
                <InputField
                  label="Inst. No."
                  placeholder="Enter instrument / cheque number"
                  value={form.instNo}
                  onChange={(e) => setForm({ ...form, instNo: e.target.value })}
                />
                <InputField
                  label="Inst. Date"
                  type="date"
                  value={form.instDate}
                  onChange={(e) => setForm({ ...form, instDate: e.target.value })}
                />
                <InputField
                  label="Drawn On"
                  placeholder="Bank / branch name"
                  value={form.drawnOn}
                  onChange={(e) => setForm({ ...form, drawnOn: e.target.value })}
                />
                <label className="grid gap-1.5">
                  <Label>Bill Amt</Label>
                  <div className="flex gap-2">
                    <input
                      type="number"
                      placeholder="0.00"
                      value={form.billAmt}
                      onChange={(e) => setForm({ ...form, billAmt: e.target.value })}
                      className="flex-1 h-9 rounded-xl border border-outline-variant/30 bg-surface-container-low px-3 text-xs text-on-surface outline-none placeholder:text-on-surface-variant/50 focus:border-primary focus:bg-surface focus:ring-2 focus:ring-primary/20"
                    />
                    <button
                      type="button"
                      onClick={selectAll}
                      className="inline-flex items-center gap-1 rounded-xl bg-emerald-600 px-3 text-xs font-bold text-white hover:bg-emerald-700 transition-colors shadow-xs cursor-pointer"
                      title="Auto-fill all installments"
                    >
                      <span className="material-symbols-outlined text-[14px]">auto_fix_high</span>
                      FIFO
                    </button>
                  </div>
                </label>
              </div>
            </div>

            {/* 3. Voucher Details */}
            <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-5 shadow-sm space-y-4">
              <h3 className="text-xs font-black uppercase tracking-[0.14em] text-primary border-b border-outline-variant/15 pb-2">
                Voucher Details
              </h3>
              <div className="space-y-3.5">
                <InputField
                  label="Company"
                  value={form.company}
                  onChange={(e) => setForm({ ...form, company: e.target.value })}
                />
                <div className="grid grid-cols-2 gap-3">
                  <InputField
                    label="Branch"
                    value={form.branch}
                    onChange={(e) => setForm({ ...form, branch: e.target.value })}
                  />
                  <InputField
                    label="Dept."
                    value={form.dept}
                    onChange={(e) => setForm({ ...form, dept: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <SelectFieldInline
                    label="Type"
                    required
                    options={['Cash', 'Cheque', 'DD', 'NEFT', 'RTGS', 'UPI']}
                    value={form.type}
                    onChange={(e) => setForm({ ...form, type: e.target.value })}
                  />
                  <InputField label="No." value="Auto" disabled />
                </div>
                <InputField
                  label="Date"
                  type="date"
                  value={form.date}
                  onChange={(e) => setForm({ ...form, date: e.target.value })}
                />
                <button
                  type="button"
                  onClick={handleGetBills}
                  disabled={loadingBills}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary/10 px-4 py-2 text-xs font-bold text-primary hover:bg-primary/20 transition-all cursor-pointer"
                >
                  <span className="material-symbols-outlined text-[16px]">{loadingBills ? 'progress_activity' : 'receipt_long'}</span>
                  {loadingBills ? 'Loading Bills...' : 'Refresh Bills'}
                </button>
                {billsMsg && <p className="text-[11px] font-semibold text-error">{billsMsg}</p>}
              </div>
            </div>
          </div>

          {/* ── Pending Installments Table ── */}
          <div className="overflow-hidden rounded-2xl border border-outline-variant/30 bg-surface-container-lowest shadow-sm">
            <div className="flex items-center justify-between border-b border-outline-variant/20 bg-surface-container-low px-5 py-3">
              <h3 className="text-xs font-black uppercase tracking-wider text-on-surface-variant">Pending Installments</h3>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={selectAll}
                  className="rounded-lg bg-primary/10 px-3 py-1.5 text-xs font-bold text-primary hover:bg-primary/20 transition-colors cursor-pointer"
                >
                  Select All
                </button>
                <button
                  type="button"
                  onClick={clearAll}
                  className="rounded-lg bg-error/10 px-3 py-1.5 text-xs font-bold text-error hover:bg-error/20 transition-colors cursor-pointer"
                >
                  Clear All
                </button>
              </div>
            </div>
            <table className="w-full text-left text-xs">
              <thead>
                <tr className="border-b border-outline-variant/20 bg-surface-container-low">
                  <th className="px-3 py-2.5 w-8" />
                  {['No.', 'Date', 'Due Date', 'Order No.', 'Amount', 'Balance', 'Description', 'Payment', 'Action'].map((h) => (
                    <th key={h} className="px-3 py-2.5 text-[10px] font-bold uppercase tracking-wider text-on-surface-variant">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {installments.length === 0 ? (
                  <tr>
                    <td colSpan={10} className="px-5 py-8 text-center">
                      <p className="text-xs font-semibold text-on-surface-variant">No pending installment schedules found.</p>
                    </td>
                  </tr>
                ) : (
                  installments.map((inst, idx) => {
                    const checked = selectedInstallments.has(inst.id)
                    return (
                      <tr
                        key={inst.id}
                        className={`border-b border-outline-variant/10 transition-colors hover:bg-surface-container-low/40 ${
                          checked ? 'bg-primary/5' : idx % 2 === 0 ? 'bg-white' : 'bg-surface-container-low/20'
                        }`}
                      >
                        <td className="px-3 py-2.5">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => toggleInstallment(inst.id)}
                            className="h-4 w-4 rounded border-outline-variant text-primary focus:ring-primary/20 cursor-pointer"
                          />
                        </td>
                        <td className="px-3 py-2.5 font-bold text-on-surface">{inst.no}</td>
                        <td className="px-3 py-2.5 text-on-surface-variant">{inst.date}</td>
                        <td className="px-3 py-2.5 text-on-surface">{inst.dueDate}</td>
                        <td className="px-3 py-2.5 font-mono text-on-surface-variant">{inst.orderNo}</td>
                        <td className="px-3 py-2.5 font-mono font-bold text-on-surface">₹{fmt(inst.amount)}</td>
                        <td className="px-3 py-2.5 font-mono font-bold text-primary">₹{fmt(inst.balance)}</td>
                        <td className="px-3 py-2.5 text-on-surface-variant">{inst.description}</td>
                        <td className="px-3 py-2.5">
                          <input
                            type="number"
                            value={paymentAmounts[inst.id] || ''}
                            onChange={(e) => setPaymentAmounts((p) => ({ ...p, [inst.id]: e.target.value }))}
                            disabled={!checked}
                            placeholder="0.00"
                            className="h-8 w-28 rounded-lg border border-outline-variant/30 bg-surface-container-low px-2.5 text-xs text-on-surface outline-none focus:border-primary focus:bg-surface focus:ring-1 focus:ring-primary/20 disabled:opacity-40"
                          />
                        </td>
                        <td className="px-3 py-2.5">
                          <select
                            value={writeOff[inst.id] || ''}
                            onChange={(e) => setWriteOff((p) => ({ ...p, [inst.id]: e.target.value }))}
                            disabled={!checked}
                            className="h-8 rounded-lg border border-outline-variant/30 bg-surface-container-low px-2 text-xs text-on-surface outline-none disabled:opacity-40"
                          >
                            <option value="">Apply Full</option>
                            <option value="write-off">Write Off</option>
                            <option value="carry">Carry Forward</option>
                          </select>
                        </td>
                      </tr>
                    )
                  })
                )}
                {selectedInstallments.size > 0 && (
                  <tr className="bg-surface-container-low font-bold">
                    <td colSpan={6} className="px-4 py-3 text-right text-xs uppercase tracking-wider text-on-surface-variant">
                      Total Selected Payment:
                    </td>
                    <td colSpan={4} className="px-4 py-3 font-mono text-sm font-black text-primary">
                      ₹{totals.amount}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* ── Narration, Attachments, Totals ── */}
          <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
            <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-5 shadow-sm space-y-4 lg:col-span-2">
              <h3 className="text-xs font-black uppercase tracking-[0.14em] text-primary border-b border-outline-variant/15 pb-2">
                Narration &amp; Attachments
              </h3>
              <TextAreaField
                label="Narration"
                placeholder="Enter remarks or fee particulars..."
                value={form.narration}
                onChange={(val) => setForm({ ...form, narration: val })}
              />
              <label className="grid gap-1.5">
                <Label>Attach Payment Proof / Cheque Scan (PDF/JPG)</Label>
                <div className="flex items-center gap-3">
                  <input
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={(e) => setFileName(e.target.files?.[0]?.name || '')}
                    className="hidden"
                    id="receipt-file-upload"
                  />
                  <label
                    htmlFor="receipt-file-upload"
                    className="inline-flex cursor-pointer items-center gap-2 rounded-xl border border-outline-variant/30 bg-surface-container-low px-4 py-2 text-xs font-bold text-on-surface hover:bg-surface-container transition-all"
                  >
                    <span className="material-symbols-outlined text-[16px] text-primary">upload_file</span>
                    Choose Document
                  </label>
                  {fileName ? (
                    <span className="inline-flex items-center gap-1.5 rounded-lg bg-primary/10 px-3 py-1.5 text-xs font-semibold text-primary">
                      <span className="material-symbols-outlined text-[14px]">description</span>
                      {fileName}
                    </span>
                  ) : (
                    <span className="text-xs text-on-surface-variant/60">No file selected</span>
                  )}
                </div>
              </label>
            </div>

            <div className="rounded-2xl border border-outline-variant/20 bg-surface-container-lowest p-5 shadow-sm space-y-3">
              <h3 className="text-xs font-black uppercase tracking-[0.14em] text-primary border-b border-outline-variant/15 pb-2">
                Totals Summary
              </h3>
              <div className="flex items-center justify-between rounded-xl bg-surface-container-low px-4 py-2.5">
                <span className="text-xs font-bold text-on-surface-variant">Sub Total</span>
                <span className="text-sm font-black text-on-surface font-mono">₹{totals.amount}</span>
              </div>
              <InputField
                label="Bank Charges"
                type="number"
                value={bankCharges}
                onChange={(e) => setBankCharges(e.target.value)}
              />
              <InputField
                label="GST Amount"
                type="number"
                value={gstAmount}
                onChange={(e) => setGstAmount(e.target.value)}
              />
              <InputField
                label="TDS"
                type="number"
                value={tds}
                onChange={(e) => setTds(e.target.value)}
              />
              <div className="flex items-center justify-between rounded-xl bg-primary px-4 py-3 text-on-primary">
                <span className="text-xs font-bold uppercase tracking-wider">Net Amount</span>
                <span className="text-base font-black font-mono">₹{totals.total}</span>
              </div>
            </div>
          </div>

          {/* ── Form Actions Bar ── */}
          <div className="flex items-center justify-between rounded-2xl border border-outline-variant/20 bg-surface-container-low px-6 py-4">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={openHistory}
                className="inline-flex items-center gap-1.5 rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-4 py-2 text-xs font-bold text-on-surface hover:bg-surface-container transition-all cursor-pointer"
              >
                <span className="material-symbols-outlined text-[16px]">history</span>
                History
              </button>
              {saveMsg && <p className="text-xs font-semibold text-error">{saveMsg}</p>}
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={onBack}
                className="rounded-xl border border-outline-variant/30 bg-surface-container-lowest px-5 py-2 text-xs font-bold text-on-surface-variant hover:bg-surface-container transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-2 text-xs font-bold text-on-primary shadow-md shadow-primary/20 hover:bg-primary/90 transition-all cursor-pointer disabled:opacity-60"
              >
                <span className="material-symbols-outlined text-[16px]">{saving ? 'progress_activity' : 'save'}</span>
                {saving ? 'Saving...' : 'Save Receipt'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
